"use client";

import { useEffect } from "react";
import { clearReloadFlag, isChunkLoadError, recoverFromChunkError } from "@/lib/chunkRecovery";

/**
 * Catches the "your tab is older than the server" error anywhere on the page.
 *
 * React's error boundaries only see throws that happen during render. A chunk
 * that fails to load usually does not: it comes back as a rejected promise
 * inside webpack's loader, and lands in the console as an *uncaught* error
 * while the UI simply stops responding to whatever was clicked. So the boundary
 * is not enough on its own — this listens at the window, where those actually
 * arrive.
 *
 * See lib/chunkRecovery for why it reloads exactly once.
 */
export function ChunkErrorRecovery() {
  useEffect(() => {
    // Reaching a rendered page means the app is loadable, so a previous
    // recovery is spent and a future deploy may use it again.
    clearReloadFlag();

    const onError = (event: ErrorEvent) => {
      if (!isChunkLoadError(event.error ?? event.message)) return;
      recoverFromChunkError(event.error ?? event.message);
    };
    const onRejection = (event: PromiseRejectionEvent) => {
      if (!isChunkLoadError(event.reason)) return;
      recoverFromChunkError(event.reason);
    };

    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);
    return () => {
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
    };
  }, []);

  return null;
}
